import {
  Col,
  DotNavigation,
  Row,
  SkeletonDots,
  useIsMobile,
  YesObject
} from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useRef, useState } from 'react'

import DetailsSection from 'component/DetailsSection'
import { ScrollToViewHandle } from 'component/DetailsSection/DetailsSection.types'
import EmptyStates from 'component/EmptyStates/EmptyStates'
import { useIsOpen } from 'shared/hooks'
import usePreloadingImages from 'shared/hooks/usePreloadingImages/usePreloadingImages'
import { StyledGrid } from 'shared/style/grid'
import { generateSummary } from 'shared/utils/string/summary'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { SafeHtmlContent } from 'site/shared/components/SafeHtmlContent'
import { useSiteState } from 'siteState/shared'
import SummaryDetailsCarouselButton, {
  getSummaryDetailsCarouselButtonAttribution
} from './SummaryDetailsCarouselButton'
import {
  Content,
  DetailsSectionContainer,
  Image,
  Navigation,
  StyledHierarchyCard,
  Summary,
  Title
} from './SummaryDetailsCarouselContainer.styles'
import { SummaryDetailsCarouselContainerProps } from './SummaryDetailsCarouselContainer.types'

const SummaryDetailsCarouselContainer = ({
  props
}: SummaryDetailsCarouselContainerProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollToDiv, setScrollToDiv] = useState(false)

  const { isOpen, open, close } = useIsOpen(false)

  const detailsRef = useRef<ScrollToViewHandle>(null)

  const styleProps = useStyleMaps(props.styleMap)

  const isMobile = useIsMobile()

  const {
    items,
    button,
    summary,
    dotNavigationColor,
    titlePosition = 'top',
    imageWidthColumns = 6
  } = props

  const currentItem = items?.[selectedIndex] ?? {}

  const buttonAttribution = getSummaryDetailsCarouselButtonAttribution(
    button,
    currentItem.uuid
  )

  const documentUUIDs = items
    ?.map((item) => (item?.documento?.[0] as YesObject)?.uuid)
    .filter(Boolean)

  const images = usePreloadingImages({ documentUUIDs })

  const documentUuid = (currentItem?.documento?.[0] as YesObject)?.uuid
  const image = images?.find((image) => image?.[documentUuid])

  const onNavigate = useCallback((index: number) => {
    setSelectedIndex(index)
  }, [])

  const onReadMore = useCallback(() => {
    open()
    setScrollToDiv(true)
  }, [open])

  const isObjectNotEmpty = Object.keys(currentItem).length > 0

  const [allResultsFetched] = useSiteState('allResultsFetched')
  const isLoading = !allResultsFetched

  useEffect(() => {
    if (!detailsRef.current) return
    if (isOpen && scrollToDiv) {
      detailsRef.current.scrollToContent()
      setScrollToDiv(false)
    }
  }, [isOpen, scrollToDiv])

  if (isLoading) {
    return (
      <StyledGrid style={styleProps}>
        <Row style={{ justifyContent: 'center' }}>
          <Col lg={12}>
            <SkeletonDots />
          </Col>
        </Row>
      </StyledGrid>
    )
  }

  if (!isObjectNotEmpty) {
    return (
      <StyledGrid style={styleProps}>
        <Row style={{ justifyContent: 'center' }}>
          <Col lg={12}>
            <EmptyStates state='no-data-to-view' showComplement={false} />
          </Col>
        </Row>
      </StyledGrid>
    )
  }

  const titlePositionInBreakpoint = isMobile ? 'top' : titlePosition

  const isTitleTop = titlePositionInBreakpoint === 'top'
  const isTitleTopRight = titlePositionInBreakpoint === 'top-right'

  return (
    <StyledGrid style={styleProps}>
      <Row style={{ justifyContent: 'center' }}>
        <Col lg={12}>
          <StyledHierarchyCard>
            {currentItem.des && isTitleTop && <Title>{currentItem.des}</Title>}
            <Content>
              {image && image[documentUuid] && (
                <Col md={imageWidthColumns}>
                  <Image>
                    <img src={image[documentUuid]} alt={currentItem.des} />
                  </Image>
                </Col>
              )}
              <Summary>
                {currentItem.des && isTitleTopRight && (
                  <Title position={titlePositionInBreakpoint}>
                    {currentItem.des}
                  </Title>
                )}

                {currentItem.complement &&
                  (summary ? (
                    generateSummary(currentItem.complement, summary?.max_length)
                  ) : (
                    <SafeHtmlContent html={currentItem.complement} />
                  ))}

                <SummaryDetailsCarouselButton
                  attribution={buttonAttribution}
                  onReadMore={onReadMore}
                />
              </Summary>
            </Content>
          </StyledHierarchyCard>

          {items.length > 1 && (
            <Navigation>
              <DotNavigation
                length={items.length}
                onSelect={onNavigate}
                selected={selectedIndex}
                disabled={items.length <= 1}
                color={dotNavigationColor}
              />
            </Navigation>
          )}

          {isOpen && (
            <DetailsSectionContainer>
              <DetailsSection
                isOpen={isOpen}
                onClose={close}
                des={currentItem.des}
                complement={currentItem.complement}
                ref={detailsRef}
              />
            </DetailsSectionContainer>
          )}
        </Col>
      </Row>
    </StyledGrid>
  )
}

export default SummaryDetailsCarouselContainer
